import { extend, $$ } from './HelperFunctions'

const isFunction = (value) => typeof value === 'function'
const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

class GalleryFlowSlider {
  constructor (container, options) {
    if (!container || !(container instanceof HTMLElement)) {
      return
    }

    this.root = container
    this.options = extend(true, {}, options)
    this.runtimeOptions = extend(true, {}, this.options)

    this.stage = null
    this.slides = []
    this.prevButton = null
    this.nextButton = null
    this.pagination = null
    this.paginationButtons = []

    this.currentIndex = 0
    this.previousIndex = 0
    this.autoplayTimer = null
    this.isDestroyed = false
    this.boundListeners = []
    this.slideRelatives = []

    this.drag = {
      active: false,
      startX: 0,
      startY: 0,
      pointerId: null
    }

    this.init()
  }

  init () {
    this.resolveElements()

    if (!this.stage || this.slides.length < 1) {
      return
    }

    this.applyA11yBase()

    this.currentIndex = clamp(this.options.startIndex || 0, 0, Math.max(0, this.slides.length - 1))
    this.previousIndex = this.currentIndex

    this.applyBreakpoints()
    this.buildPagination()
    this.bindControls()
    this.bindKeyboard()
    this.bindPointer()
    this.bindHoverPause()
    this.bindResize()

    this.render(true)
    this.updateControlState()

    if (this.runtimeOptions.autoplay) {
      this.startAutoplay()
    }

    this.root.classList.add('is-ready', 'gallery--ready')

    if (isFunction(this.runtimeOptions.onInit)) {
      this.runtimeOptions.onInit(this)
    }
  }

  resolveElements () {
    const selectors = this.options.selectors

    this.stage = this.root.querySelector(selectors.stage)
    this.slides = this.stage ? [...this.stage.querySelectorAll(selectors.slide)] : []
    this.prevButton = this.root.querySelector(selectors.prevButton)
    this.nextButton = this.root.querySelector(selectors.nextButton)
    this.pagination = this.root.querySelector(selectors.pagination)
  }

  applyA11yBase () {
    this.root.setAttribute('role', 'region')

    if (!this.root.getAttribute('aria-label')) {
      this.root.setAttribute('aria-label', 'Galerie Slider')
    }

    if (this.prevButton && !this.prevButton.getAttribute('aria-label')) {
      this.prevButton.setAttribute('aria-label', 'Vorheriges Bild')
    }

    if (this.nextButton && !this.nextButton.getAttribute('aria-label')) {
      this.nextButton.setAttribute('aria-label', 'Nächstes Bild')
    }

    if (!this.root.hasAttribute('tabindex')) {
      this.root.setAttribute('tabindex', '0')
    }
  }

  applyBreakpoints () {
    const viewport = window.innerWidth || document.documentElement.clientWidth
    const breakpoints = this.options.breakpoints || {}

    const keys = Object.keys(breakpoints)
      .map((key) => parseInt(key, 10))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b)

    let merged = extend(true, {}, this.options)

    keys.forEach((bp) => {
      if (viewport <= bp) {
        merged = extend(true, merged, breakpoints[bp])
      }
    })

    this.runtimeOptions = merged
  }

  buildPagination () {
    if (!this.pagination) {
      this.pagination = document.createElement('div')
      this.pagination.className = 'gallery__pagination'

      const controls = this.root.querySelector('.gallery__controls')
      if (controls) {
        controls.appendChild(this.pagination)
      } else {
        this.root.appendChild(this.pagination)
      }
    }
    this.pagination.classList.add('gallery__pagination')

    this.pagination.innerHTML = ''
    this.paginationButtons = []

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button')
      dot.type = 'button'
      dot.className = 'gallery__dot'
      dot.setAttribute('aria-label', `Gehe zu Bild ${index + 1}`)

      dot.addEventListener('click', () => {
        this.goTo(index)
      })

      this.pagination.appendChild(dot)
      this.paginationButtons.push(dot)
    })

    this.pagination.setAttribute('role', 'tablist')
  }

  bindControls () {
    if (this.prevButton) {
      this.on(this.prevButton, 'click', () => {
        this.prev()
      })
    }

    if (this.nextButton) {
      this.on(this.nextButton, 'click', () => {
        this.next()
      })
    }
  }

  bindKeyboard () {
    if (!this.runtimeOptions.keyboardNavigation) {
      return
    }

    this.on(this.root, 'keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prev()
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.next()
      }
    })
  }

  bindPointer () {
    if (!this.runtimeOptions.swipe && !this.runtimeOptions.draggable) {
      return
    }

    if (window.PointerEvent) {
      this.bindPointerEvents()
      return
    }

    this.bindTouchEvents()
    this.bindMouseEvents()
  }

  bindPointerEvents () {
    this.on(this.stage, 'pointerdown', (event) => {
      const isTouch = event.pointerType === 'touch'
      const isMouse = event.pointerType === 'mouse'

      if ((isTouch && !this.runtimeOptions.swipe) || (isMouse && !this.runtimeOptions.draggable)) {
        return
      }

      this.drag.active = true
      this.drag.startX = event.clientX
      this.drag.startY = event.clientY
      this.drag.pointerId = event.pointerId

      this.stage.classList.add('gallery--dragging')
      this.stage.setPointerCapture(event.pointerId)
    })

    this.on(this.stage, 'pointerup', (event) => {
      if (!this.drag.active || this.drag.pointerId !== event.pointerId) {
        return
      }

      const dx = event.clientX - this.drag.startX
      const dy = event.clientY - this.drag.startY
      const threshold = 40

      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')

      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    })

    this.on(this.stage, 'pointercancel', () => {
      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')
    })
  }

  bindTouchEvents () {
    this.on(this.stage, 'touchstart', (event) => {
      if (!this.runtimeOptions.swipe) {
        return
      }

      const touch = event.changedTouches && event.changedTouches[0]
      if (!touch) {
        return
      }

      this.drag.active = true
      this.drag.startX = touch.clientX
      this.drag.startY = touch.clientY
      this.drag.pointerId = null
      this.stage.classList.add('gallery--dragging')
    }, { passive: true })

    this.on(this.stage, 'touchend', (event) => {
      if (!this.drag.active) {
        return
      }

      const touch = event.changedTouches && event.changedTouches[0]
      if (!touch) {
        return
      }

      const dx = touch.clientX - this.drag.startX
      const dy = touch.clientY - this.drag.startY
      const threshold = 30

      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')

      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    }, { passive: true })

    this.on(this.stage, 'touchcancel', () => {
      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')
    })
  }

  bindMouseEvents () {
    this.on(this.stage, 'mousedown', (event) => {
      if (!this.runtimeOptions.draggable) {
        return
      }

      this.drag.active = true
      this.drag.startX = event.clientX
      this.drag.startY = event.clientY
      this.drag.pointerId = null
      this.stage.classList.add('gallery--dragging')
    })

    this.on(this.stage, 'mouseup', (event) => {
      if (!this.drag.active) {
        return
      }

      const dx = event.clientX - this.drag.startX
      const dy = event.clientY - this.drag.startY
      const threshold = 30

      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')

      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    })

    this.on(this.stage, 'mouseleave', () => {
      this.drag.active = false
      this.stage.classList.remove('gallery--dragging')
    })
  }

  bindHoverPause () {
    if (!this.runtimeOptions.autoplay || !this.runtimeOptions.pauseOnHover) {
      return
    }

    this.on(this.root, 'mouseenter', () => {
      this.stopAutoplay()
    })

    this.on(this.root, 'mouseleave', () => {
      this.startAutoplay()
    })
  }

  bindResize () {
    this.on(window, 'resize', () => {
      this.update()
    })
  }

  on (element, eventName, handler, options) {
    element.addEventListener(eventName, handler, options || false)
    this.boundListeners.push({
      element,
      eventName,
      handler,
      options: options || false
    })
  }

  offAll () {
    this.boundListeners.forEach((entry) => {
      entry.element.removeEventListener(entry.eventName, entry.handler, entry.options)
    })

    this.boundListeners = []
  }

  startAutoplay () {
    this.stopAutoplay()

    this.autoplayTimer = window.setInterval(() => {
      this.next()
    }, this.runtimeOptions.autoplayDelay)
  }

  stopAutoplay () {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  resolveOffset () {
    const value = this.runtimeOptions.offsetStep

    if (typeof value === 'string') {
      return value
    }

    if (typeof value === 'number' && value <= 1) {
      return `${Math.round(this.stage.clientWidth * value)}px`
    }

    return `${Math.round(value)}px`
  }

  relativePosition (slideIndex, activeIndex) {
    const delta = slideIndex - activeIndex

    if (!this.runtimeOptions.loop) {
      return delta
    }

    const total = this.slides.length
    let wrapped = delta

    if (Math.abs(delta) > total / 2) {
      wrapped = delta > 0 ? delta - total : delta + total
    }

    return wrapped
  }

  render (isInit) {
    const options = this.runtimeOptions
    const offsetUnit = this.resolveOffset()

    this.root.style.setProperty('--gallery1-duration', `${options.animationDuration}ms`)
    this.root.style.setProperty('--gallery1-easing', options.animationEasing)

    this.slides.forEach((slide, index) => {
      const relative = this.relativePosition(index, this.currentIndex)
      const prevRelative = this.slideRelatives[index]
      const distance = Math.abs(relative)
      const visible = distance <= options.visibleDepth
      const scale = Math.max(0.4, 1 - distance * options.scaleStep)
      const opacity = visible ? Math.max(0, 1 - distance * options.opacityStep) : 0
      const zIndex = Math.max(1, options.zIndexBase - distance)
      let translateX

      if (typeof offsetUnit === 'string' && offsetUnit.indexOf('px') > -1) {
        translateX = relative * parseFloat(offsetUnit)
      } else if (typeof offsetUnit === 'string') {
        translateX = `calc(${relative} * ${offsetUnit})`
      } else {
        translateX = relative * offsetUnit
      }

      const translateY = distance * options.yStep

      slide.style.zIndex = String(zIndex)
      slide.style.opacity = String(opacity)
      slide.style.visibility = visible ? 'visible' : 'hidden'
      slide.style.pointerEvents = distance === 0 ? 'auto' : 'none'
      slide.style.transition = ''

      if (options.centered) {
        if (typeof translateX === 'number') {
          slide.style.transform = `translate(-50%, -50%) translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`
        } else {
          slide.style.transform = `translate(-50%, -50%) translateX(${translateX}) translateY(${translateY}px) scale(${scale})`
        }
      } else if (typeof translateX === 'number') {
        slide.style.transform = `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`
      } else {
        slide.style.transform = `translateX(${translateX}) translateY(${translateY}px) scale(${scale})`
      }

      slide.setAttribute('aria-hidden', distance === 0 ? 'false' : 'true')
      slide.tabIndex = distance === 0 ? 0 : -1
      slide.classList.toggle('is-active', distance === 0)
      this.slideRelatives[index] = relative

      if (!isInit && options.loop && typeof prevRelative === 'number' && Math.abs(prevRelative - relative) > options.visibleDepth + 1) {
        slide.style.transition = 'none'
        window.requestAnimationFrame(() => {
          slide.style.transition = ''
        })
      }
    })

    this.paginationButtons.forEach((dot, index) => {
      const active = index === this.currentIndex
      dot.classList.toggle('is-active', active)
      dot.setAttribute('aria-selected', active ? 'true' : 'false')
    })
  }

  updateControlState () {
    if (!this.prevButton || !this.nextButton) {
      return
    }

    if (this.runtimeOptions.loop) {
      this.prevButton.disabled = false
      this.nextButton.disabled = false
      return
    }

    this.prevButton.disabled = this.currentIndex <= 0
    this.nextButton.disabled = this.currentIndex >= this.slides.length - 1
  }

  changeTo (nextIndex) {
    if (this.isDestroyed || this.slides.length < 1) {
      return
    }

    const total = this.slides.length
    let target = nextIndex

    if (this.runtimeOptions.loop) {
      target = (nextIndex + total) % total
    } else {
      target = clamp(nextIndex, 0, total - 1)
    }

    if (target === this.currentIndex) {
      return
    }

    this.previousIndex = this.currentIndex
    this.currentIndex = target

    this.render(false)
    this.updateControlState()

    if (isFunction(this.runtimeOptions.onChange)) {
      this.runtimeOptions.onChange({
        index: this.currentIndex,
        previousIndex: this.previousIndex,
        slide: this.slides[this.currentIndex]
      })
    }
  }

  next () {
    this.changeTo(this.currentIndex + 1)
  }

  prev () {
    this.changeTo(this.currentIndex - 1)
  }

  goTo (index) {
    this.changeTo(index)
  }

  update () {
    if (this.isDestroyed) {
      return
    }

    this.resolveElements()
    this.applyBreakpoints()

    if (this.currentIndex > this.slides.length - 1) {
      this.currentIndex = Math.max(0, this.slides.length - 1)
    }

    this.buildPagination()
    this.render(false)
    this.updateControlState()
  }

  destroy () {
    if (this.isDestroyed) {
      return
    }

    this.stopAutoplay()
    this.offAll()

    this.slides.forEach((slide) => {
      slide.removeAttribute('style')
      slide.removeAttribute('aria-hidden')
      slide.classList.remove('is-active')
      slide.tabIndex = -1
    })

    this.paginationButtons.forEach((dot) => {
      dot.removeAttribute('aria-selected')
    })

    this.root.classList.remove('is-ready', 'gallery--ready')
    this.root.style.removeProperty('--gallery1-duration')
    this.root.style.removeProperty('--gallery1-easing')

    this.isDestroyed = true

    if (isFunction(this.runtimeOptions.onDestroy)) {
      this.runtimeOptions.onDestroy(this)
    }
  }
}

export class Gallery1 {
  constructor (selectors, options) {
    this.selectors = extend({
      block: '.gallery--1',
      stage: '.gallery__list',
      slide: '.gallery__item',
      prevButton: '.gallery__prev, .gallery__previous',
      nextButton: '.gallery__next',
      pagination: '.gallery__pagination, .gallery_pagination, .gallery__dots'
    }, selectors)

    this.options = extend(true, {
      startIndex: 0,
      loop: true,
      autoplay: false,
      autoplayDelay: 3500,
      pauseOnHover: true,
      keyboardNavigation: true,
      swipe: true,
      draggable: false,
      animationDuration: 480,
      animationEasing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
      visibleDepth: 2,
      scaleStep: 0.2,
      offsetStep: 0.21,
      yStep: 6,
      opacityStep: 0.2,
      zIndexBase: 100,
      centered: true,
      breakpoints: {
        900: {
          visibleDepth: 1,
          offsetStep: 0.21,
          yStep: 10
        },
        640: {
          visibleDepth: 1,
          offsetStep: 0.21,
          yStep: 10
        }
      }
    }, options)

    this.instances = []
  }

  init () {
    const galleries = $$(this.selectors.block)

    if (!galleries.length) {
      return
    }

    galleries.forEach((gallery) => {
      if (gallery.dataset.gallery1Initialized === 'true') {
        return
      }

      const slider = new GalleryFlowSlider(gallery, extend(true, {}, this.options, {
        selectors: this.selectors
      }))

      if (slider && slider.stage) {
        this.instances.push(slider)
        gallery.dataset.gallery1Initialized = 'true'
      }
    })
  }
}

export default Gallery1
